import { DropoffRateOption } from "@redotech/merchant-sdk/rpc/schema/get-dropoff-options";
import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import { Rate } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { merchantAddressSupportsProvider } from "@redotech/redo-return-operations-common/dropoff/eligibility";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { getDatadogActionName } from "@redotech/redo-web/datadog-action-name";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { useLocationOptions } from "../../../hooks/use-location-options";
import type { CreateLabelForm } from "../create-label-modal-forms";
import type { AddressOption } from "../create-label-modal-types";
import { Page } from "../create-label-modal-types";
import {
  findSelectedAddressOption,
  getReturnShippingPriceDescription,
  getShipAddressOptions,
} from "../create-label-modal-utils";
import { AddressDropdown } from "./address-dropdown";
import { CarrierSelect } from "./carrier-select";
import { DropoffRate } from "./dropoff-rate";
import { GroupProductsList } from "./group-products-list";
import { LabelWeightInput } from "./label-weight-input";

type ReturnProduct = MerchantAppReturn["products"][number];
type ReturnMethod = "label" | "dropoff";

interface CreateLabelFormContentProps {
  "data-dd-action-name"?: string;
  return_: MerchantAppReturn;
  shipmentNumber: number | undefined;
  shipmentId: string | undefined;
  groupProducts: ReturnProduct[] | undefined;
  createLabelInput: CreateLabelForm;
  setPage: (page: Page) => void;
  labelRateInUse: Rate | undefined;
  labelRatesLoading: boolean;
  dropoffPriceLoading: boolean;
  rates: Rate[];
  optionLabelType: PurchaseLabelType;
  optionOriginalLabelCost: number;
  isPending: boolean;
  isReShipment: boolean;
  dropoffOptions: DropoffRateOption[];
  dropoffEnabledForTeam: boolean;
  isFirstLabel: boolean;
  isFirstLabelOrReplacingFirstLabel: boolean;
  redoMaxLabelPrice: number;
}

export const CreateLabelFormContent = memo(function CreateLabelFormContent({
  return_,
  "data-dd-action-name": actionName,
  shipmentNumber,
  shipmentId,
  groupProducts,
  createLabelInput,
  setPage,
  labelRateInUse,
  labelRatesLoading,
  dropoffPriceLoading,
  rates,
  optionLabelType,
  optionOriginalLabelCost,
  isPending,
  isReShipment,
  dropoffOptions,
  dropoffEnabledForTeam,
  isFirstLabel,
  isFirstLabelOrReplacingFirstLabel,
  redoMaxLabelPrice,
}: CreateLabelFormContentProps) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const { formatCurrency } = useContext(CurrencyContext);
  const locationOptionsQuery = useLocationOptions();

  const shippingAddressOptions: RedoListItem<AddressOption>[] = useMemo(
    () =>
      getShipAddressOptions(
        return_,
        locationOptionsQuery.data,
        shipmentId,
        t,
        isReShipment,
      ),
    [return_, locationOptionsQuery.data, shipmentId, t, isReShipment],
  );

  const shipFromInput = isReShipment
    ? createLabelInput.inputs.merchantSelectedAddress
    : createLabelInput.inputs.shopperSelectedAddress;
  const shipToInput = isReShipment
    ? createLabelInput.inputs.shopperSelectedAddress
    : createLabelInput.inputs.merchantSelectedAddress;

  const shipFromSelectedItem = useMemo(
    () =>
      findSelectedAddressOption(
        shippingAddressOptions,
        shipFromInput.value,
        createLabelInput.inputs.customShipFromAddress.value,
      ),
    [
      shippingAddressOptions,
      shipFromInput.value,
      createLabelInput.inputs.customShipFromAddress.value,
    ],
  );

  const shipToSelectedItem = useMemo(
    () =>
      findSelectedAddressOption(
        shippingAddressOptions,
        shipToInput.value,
        createLabelInput.inputs.customShipToAddress.value,
      ),
    [
      shippingAddressOptions,
      shipToInput.value,
      createLabelInput.inputs.customShipToAddress.value,
    ],
  );

  const carrierSelectPlaceholder = useMemo(() => {
    const toAddress = shipToInput.value;
    if (!toAddress) {
      return t("Select ship to address to get rates");
    }
    if (labelRatesLoading) {
      return t("Getting rates...");
    }
    if (rates.length === 0) {
      return t(
        "No rates available. Please double check the address and weight.",
      );
    }
    return t("Select carrier");
  }, [shipToInput.value, labelRatesLoading, rates.length, t]);

  const dropoffSelectOptions = useMemo(
    () =>
      dropoffOptions.map((option) => ({
        id: option.provider,
        type: "custom" as const,
        value: option,
        custom: true as const,
        useWrapper: true,
        render: (
          <DropoffRate
            labelType={optionLabelType}
            option={option}
            originalLabelCost={optionOriginalLabelCost}
          />
        ),
      })),
    [dropoffOptions, optionLabelType, optionOriginalLabelCost],
  );

  const selectedShipping = createLabelInput.inputs.selectedShipping.value;
  const selectedDropoff =
    selectedShipping?.method === "dropoff"
      ? selectedShipping.dropoff
      : undefined;

  const selectedDropoffItem = useMemo(() => {
    if (dropoffPriceLoading || !selectedDropoff) {
      return undefined;
    }
    return dropoffSelectOptions.find(
      (option) => option.id === selectedDropoff.provider,
    );
  }, [dropoffSelectOptions, selectedDropoff, dropoffPriceLoading]);

  if (!team) {
    return null;
  }

  const hasDropoffOptions = dropoffOptions.length > 0;
  const dropoffUnavailableForAddress =
    dropoffEnabledForTeam && !hasDropoffOptions;
  const returnMethod: ReturnMethod = selectedShipping?.method ?? "label";
  const returnMethodOptions = [
    {
      id: "label" as const,
      label: t("Buy a shipping label"),
      description: t("Generate a carrier return label."),
    },
    {
      id: "dropoff" as const,
      label: t("Drop-off — no label needed"),
      description: t(
        "Customer drops off at a partner location for a flat fee; no label is printed.",
      ),
    },
  ];

  const handleReturnMethodChange = (method: ReturnMethod) => {
    if (method === "dropoff") {
      const firstOption = dropoffOptions[0];
      if (selectedShipping?.method !== "dropoff" && firstOption) {
        createLabelInput.inputs.selectedShipping.setValue({
          method: "dropoff",
          dropoff: firstOption,
        });
      }
    } else {
      createLabelInput.inputs.selectedShipping.setValue(undefined);
    }
  };

  const carrierSelect = (
    <CarrierSelect
      data-dd-action-name={getDatadogActionName(
        actionName,
        "create_label_carrier_{action}",
      )}
      disabled={isPending}
      isPending={labelRatesLoading}
      labelRateInUse={labelRateInUse}
      labelType={optionLabelType}
      onSelectLabel={(rate) =>
        createLabelInput.inputs.selectedShipping.setValue({
          method: "label",
          label: rate,
        })
      }
      originalLabelCost={optionOriginalLabelCost}
      placeholder={carrierSelectPlaceholder}
      rates={rates}
      return_={return_}
      team={team}
    />
  );

  const dropoffSelect = (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={isPending}
        itemsLoading={dropoffPriceLoading}
        label={t("Drop-off provider")}
        options={dropoffSelectOptions}
        optionSelected={({ item }) =>
          createLabelInput.inputs.selectedShipping.setValue({
            method: "dropoff",
            dropoff: item.value,
          })
        }
        placeholder={
          dropoffPriceLoading
            ? t("Getting rates...")
            : t("Select a drop-off provider")
        }
        selectedItem={selectedDropoffItem}
        size="xs"
      />
      {selectedDropoff && (
        <Flex>
          <RedoButton
            disabled={isPending}
            hierarchy="secondary"
            onClick={() => setPage(Page.DROPOFF_LOCATIONS)}
            pending={dropoffPriceLoading}
            size="sm"
            text={t("See drop-off locations")}
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <Flex dir="column" gap="xl">
      {return_.isManualLabel && (
        <RedoAlert
          align="flex-start"
          bgColor="primary-alt"
          color="warning"
          description={t(
            "This return was marked as manual label, which means you want to provide the label yourself outside of Redo. You can still generate a label here if needed.",
          )}
          Icon={AlertTriangleIcon}
          title={t("Manual label return")}
        />
      )}
      {(return_.type === ReturnTypeEnum.WARRANTY ||
        return_.products?.some((product) => product.strategy === "repair")) && (
        <RedoAlert
          align="flex-start"
          bgColor="primary-alt"
          color="warning"
          description={t(
            "Verify the shipping addresses. Items may need to go to the manufacturer or a repair facility rather than your normal return address.",
          )}
          Icon={AlertTriangleIcon}
          title={t("Warranty or repair items")}
        />
      )}

      {shipmentNumber && (
        <Text fontSize="xs" fontWeight="semibold" textColor="primary">
          Shipment {shipmentNumber}
        </Text>
      )}

      {groupProducts && groupProducts.length > 0 && (
        <GroupProductsList
          formatCurrency={formatCurrency}
          products={groupProducts}
        />
      )}

      <LabelWeightInput
        data-dd-action-name={actionName}
        labelWeightForm={createLabelInput.inputs.labelWeight}
        loading={isPending}
      />

      <Flex dir="column" gap="xl">
        <AddressDropdown
          data-dd-action-name={getDatadogActionName(
            actionName,
            "create_label_ship_from_{action}",
          )}
          disabled={isPending}
          label={t("Ship from")}
          onOptionSelected={(value: AddressOption) => {
            if (value === "custom") {
              setPage(Page.CUSTOM_SHIP_FROM);
            } else {
              shipFromInput.setValue(value);
              if (isReShipment) {
                createLabelInput.inputs.merchantAddress.setValue(value);
              } else {
                createLabelInput.inputs.shopperFinalAddress.setValue(value);
              }
            }
          }}
          options={shippingAddressOptions}
          placeholder={t("Select an address...")}
          selectedItem={shipFromSelectedItem}
          size="xs"
        />

        <AddressDropdown
          data-dd-action-name={getDatadogActionName(
            actionName,
            "create_label_ship_to_{action}",
          )}
          disabled={isPending}
          label={t("Ship to")}
          onOptionSelected={(value: AddressOption) => {
            if (value === "custom") {
              setPage(Page.CUSTOM_SHIP_TO);
            } else {
              shipToInput.setValue(value);
              if (isReShipment) {
                createLabelInput.inputs.shopperFinalAddress.setValue(value);
              } else {
                createLabelInput.inputs.merchantAddress.setValue(value);
              }
              if (
                selectedDropoff &&
                !merchantAddressSupportsProvider(
                  selectedDropoff.provider,
                  value,
                )
              ) {
                createLabelInput.inputs.selectedShipping.setValue(undefined);
              }
            }
          }}
          options={shippingAddressOptions}
          placeholder={t("Select an address...")}
          selectedItem={shipToSelectedItem}
          size="xs"
        />
      </Flex>

      {hasDropoffOptions ? (
        <Flex dir="column" gap="lg">
          <RedoRadioGroup
            disabled={isPending}
            label={t("Return method")}
            options={returnMethodOptions}
            setValue={handleReturnMethodChange}
            size="sm"
            value={returnMethod}
          />
          {returnMethod === "label" ? carrierSelect : dropoffSelect}
        </Flex>
      ) : dropoffUnavailableForAddress ? (
        <Flex dir="column" gap="md">
          {carrierSelect}
          <Text fontSize="xs" textColor="secondary">
            {t("No drop-off options available for this address.")}
          </Text>
        </Flex>
      ) : (
        carrierSelect
      )}

      {returnMethod === "label" && (
        <RedoAlert
          align="flex-start"
          bgColor="primary-alt"
          color="warning"
          description={getReturnShippingPriceDescription({
            noun: "label",
            labelPayer: return_.labelPayer ?? undefined,
            originalLabelCost: return_.totals?.fee ?? optionOriginalLabelCost,
            formatCurrency,
            isFirstLabelOrReplacingFirstLabel,
            isFirstLabel,
            isMerchantCarrierAccount:
              labelRateInUse?.is_merchant_carrier_account ?? false,
            redoMaxLabelPrice,
            labelDeductedFromCredit: return_.labelDeductedFromCredit ?? false,
            translate: t,
          })}
          Icon={InfoCircleIcon}
          title={t("New label price")}
        />
      )}

      {returnMethod === "dropoff" && (
        <RedoAlert
          align="flex-start"
          bgColor="primary-alt"
          color="warning"
          description={getReturnShippingPriceDescription({
            noun: "drop-off",
            labelPayer: return_.labelPayer ?? undefined,
            originalLabelCost: return_.totals?.fee ?? optionOriginalLabelCost,
            formatCurrency,
            isFirstLabelOrReplacingFirstLabel,
            isFirstLabel,
            redoMaxLabelPrice,
            labelDeductedFromCredit: return_.labelDeductedFromCredit ?? false,
            translate: t,
          })}
          Icon={InfoCircleIcon}
          title={t("Drop-off price")}
        />
      )}
    </Flex>
  );
});
